// ─────────────────────────────────────────────────────────────────────────────
// usersService.js — Enterprise users API calls
// ─────────────────────────────────────────────────────────────────────────────
import api from "./api";

/**
 * GET /users
 * Server-side search, filter, and pagination.
 *
 * @param {object} opts
 * @param {string}  opts.search   — name / email / team ILIKE
 * @param {string}  opts.role     — 'Admin'|'Security Admin'|'IT Manager'|'Auditor'|'User'
 * @param {string}  opts.status   — 'active'|'offline'|'blocked'
 * @param {string}  opts.team     — free text ILIKE
 * @param {number}  opts.page     — default 1
 * @param {number}  opts.limit    — default 20, max 100
 *
 * Returns { users: EnterpriseUserEntity[], total, page, limit }
 */
export async function getUsers(opts = {}) {
  const params = new URLSearchParams();
  if (opts.search) params.set("search", opts.search);
  if (opts.role   && opts.role   !== "All") params.set("role",   opts.role);
  if (opts.status && opts.status !== "All") params.set("status", opts.status);
  if (opts.team   && opts.team   !== "All") params.set("team",   opts.team);
  if (opts.page)  params.set("page",  String(opts.page));
  if (opts.limit) params.set("limit", String(opts.limit));
  const qs = params.toString() ? `?${params}` : "";
  return api.get(`/users${qs}`);
}

/**
 * GET /users/:id
 */
export async function getUser(id) {
  return api.get(`/users/${id}`);
}

/**
 * PATCH /users/:id/status
 * @param {string} id
 * @param {'active'|'offline'|'blocked'} status
 */
export async function updateUserStatus(id, status) {
  return api.patch(`/users/${id}/status`, { status });
}

/**
 * PATCH /users/:id/mfa
 * Enable/disable email-code MFA for a user. Requires user_management:read_write.
 *
 * MFA here is email-code, not TOTP — there is no enrollment step, so the flag is
 * the whole feature. Takes effect at that user's next login: when enabled, their
 * sign-in returns an `mfa_required` challenge and a 6-digit code is emailed.
 * Existing sessions are unaffected.
 *
 * @param {string}  id
 * @param {boolean} enabled
 * Returns { id, email, mfaEnabled }
 */
export async function updateUserMfa(id, enabled) {
  return api.patch(`/users/${id}/mfa`, { enabled });
}

/**
 * DELETE /users/:id
 */
export async function deleteUser(id) {
  return api.delete(`/users/${id}`);
}

/**
 * POST /users/invite
 * @param {{ email, role, firstName?, lastName?, team?, sendWelcomeEmail?, requireMfaSetup? }} data
 * Returns { message, invitation: { id, email, role, expiresAt, status } }
 */
export async function inviteUser(data) {
  return api.post("/users/invite", data);
}

/**
 * GET /users/invitations
 * Returns { invitations: InvitationEntity[] }
 */
export async function listInvitations() {
  return api.get("/users/invitations");
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Derives initials from a user object.
 * @param {{ firstName: string, lastName: string }} user
 */
export function userInitials(user) {
  const f = user.firstName?.[0] ?? "";
  const l = user.lastName?.[0]  ?? "";
  return (f + l).toUpperCase() || "?";
}

/**
 * Derives display name from a user object.
 */
export function userName(user) {
  return `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim() || user.email;
}

/**
 * Formats lastActiveAt or createdAt as relative time.
 */
export function formatRelative(dateStr) {
  if (!dateStr) return "—";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1)   return "just now";
  if (mins < 60)  return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)   return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30)  return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

/**
 * Formats a date as a short date string.
 */
export function formatDate(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
  });
}
